import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * A function whose identity never changes but which always runs the latest
 * `callback`. For handing a handler that closes over render state to a
 * memoised child: a fresh function every render would defeat the memo, and
 * `useCallback` with the real deps would change just as often.
 *
 * Not for calling during render — the latest callback is swapped in after
 * commit, which is what keeps a render that React throws away from leaking.
 */
export function useStableCallback<Args extends unknown[], Result>(
  callback: (...args: Args) => Result
): (...args: Args) => Result {
  const latest = useRef(callback);
  useLayoutEffect(() => {
    latest.current = callback;
  });
  return useCallback((...args: Args) => latest.current(...args), []);
}
